import React, { useCallback, useMemo, useState } from 'react'
import PapaParse from 'papaparse'
import { v4 as uuidv4 } from 'uuid'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { FileUpload } from '@amzn/stencil-react-components/file-upload'
import { Input } from '@amzn/stencil-react-components/input'
import { Col, Hr, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { Link } from '@amzn/stencil-react-components/link'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { SortDirection } from '@amzn/stencil-react-components/table'
import { H1, Label, Text } from '@amzn/stencil-react-components/text'

import { ItemContainerMetadataDTO } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { ModuleService } from 'src/services/backend/ModuleService'
import { SearchService } from 'src/services/backend/SearchService'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'
import { uploadImportSpreadsheetToS3 } from 'src/services/media/S3FileHandler'

const S3_IMPORT_PREFIX = 'item-pool-imports'

const ITEMS_CONTAINER_INFIX = '_ItemsContainer_'

const CONTAINER_COLUMN = '__container__'

const ROWS_PER_PAGE = 10

const MAX_CELL_CHARS = 40
const truncateCell = (value?: string): string =>
    value && value.length > MAX_CELL_CHARS ? `${value.slice(0, MAX_CELL_CHARS)}…` : value ?? ''

const ROW_BORDER = { borderBottom: '1px solid #E1E4EA' }

/** "1 row" / "3 rows" — count plus a naively pluralized noun. */
const pluralize = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`

const PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

/** Extract the unique {{placeholder}} token names from a serialized module. */
const extractPlaceholders = (module: ModuleDTO): string[] => {
    const json = JSON.stringify(module)
    const found = new Set<string>()
    let match: RegExpExecArray | null
    while ((match = PLACEHOLDER_REGEX.exec(json)) !== null) {
        found.add(match[1])
    }
    PLACEHOLDER_REGEX.lastIndex = 0
    return Array.from(found)
}

/**
 * Standalone "Import Content" page.
 *
 * Route: /import-content
 *
 * Simple flow: the user provides a Template Module version id, uploads a filled
 * spreadsheet (CSV), and submits. The CSV is uploaded to S3, the backend creates an
 * IMPORTED item pool from the template + spreadsheet, and the generated Item Container
 * Modules are listed.
 */
export const ImportContentPage = () => {
    const [templateModuleVersionId, setTemplateModuleVersionId] = useState('')
    const [file, setFile] = useState<File | undefined>(undefined)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [containers, setContainers] = useState<ItemContainerMetadataDTO[] | undefined>(undefined)

    const [moduleName, setModuleName] = useState<string | undefined>(undefined)
    const [placeholders, setPlaceholders] = useState<string[]>([])
    const [csvInfo, setCsvInfo] = useState<
        | {
              headers: string[]
              rowCount: number
              containerCount: number
              rows: Record<string, string>[]
          }
        | undefined
    >(undefined)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [previewPage, setPreviewPage] = useState(0)

    // Compare the uploaded CSV's columns against the template placeholders (excluding the
    // optional reserved container column) so the user can fix mismatches before submitting.
    const csvValidation = useMemo(() => {
        if (!csvInfo) {
            return undefined
        }
        const headerSet = new Set(csvInfo.headers)
        const placeholderSet = new Set(placeholders)
        const required = placeholders.filter((name) => name !== CONTAINER_COLUMN)
        return {
            missing: required.filter((name) => !headerSet.has(name)),
            extra: csvInfo.headers.filter((header) => !placeholderSet.has(header)),
        }
    }, [csvInfo, placeholders])

    const onFileAttached = useCallback((attached?: File) => {
        setFile(attached)
        setCsvInfo(undefined)
        setPreviewPage(0)
        if (!attached) {
            return
        }
        PapaParse.parse<Record<string, string>>(attached, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const headers = results.meta.fields ?? []
                const rows = results.data
                const containerCount = headers.includes(CONTAINER_COLUMN)
                    ? new Set(rows.map((row) => (row[CONTAINER_COLUMN] ?? '').trim())).size
                    : 1
                setCsvInfo({ headers, rowCount: rows.length, containerCount, rows })
            },
        })
    }, [])
    const [existingContainerNames, setExistingContainerNames] = useState<string[]>([])
    const [isLoadingModule, setIsLoadingModule] = useState(false)
    const [loadError, setLoadError] = useState<string | undefined>(undefined)

    const canSubmit =
        !!moduleName &&
        !!file &&
        !!csvValidation &&
        csvValidation.missing.length === 0 &&
        !isSubmitting

    const canPreview =
        !!moduleName &&
        (csvInfo?.rows.length ?? 0) > 0 &&
        !!csvValidation &&
        csvValidation.missing.length === 0 &&
        !isPreviewing &&
        !isSubmitting

    // Pagination bounds for the review-rows table (only meaningful once a CSV is parsed).
    const pageStart = previewPage * ROWS_PER_PAGE
    const pageCount = csvInfo ? Math.ceil(csvInfo.rows.length / ROWS_PER_PAGE) : 0

    const onLoadTemplate = useCallback(async () => {
        if (templateModuleVersionId.trim().length === 0) {
            return
        }
        setLoadError(undefined)
        setModuleName(undefined)
        setPlaceholders([])
        setExistingContainerNames([])
        setIsLoadingModule(true)

        let loadedName: string
        try {
            const moduleVersion = await ModuleService.loadModuleVersionDTO(
                templateModuleVersionId.trim()
            )
            loadedName = moduleVersion.content.name
            setModuleName(loadedName)
            const extracted = extractPlaceholders(moduleVersion.content)
            // Always surface the reserved container-grouping column alongside the placeholders.
            setPlaceholders(
                extracted.includes(CONTAINER_COLUMN) ? extracted : [...extracted, CONTAINER_COLUMN]
            )
        } catch (e) {
            console.error('Failed to load template module', e)
            setLoadError(e instanceof Error ? e.message : 'Failed to load the template module.')
            return
        } finally {
            // Stop the spinner as soon as the module load resolves — do not wait on the
            // existing-container search below (it can be slow and must not block the UI).
            setIsLoadingModule(false)
        }

        // Warn if item container modules for this template already exist (they will be overwritten).
        // Runs separately so a slow/unavailable search never keeps the load spinner going.
        try {
            const prefix = `${loadedName}${ITEMS_CONTAINER_INFIX}`
            const response = await SearchService.searchModules(
                prefix,
                {},
                { offset: 0, numberOfRecordsPerPage: 50 },
                { sortField: 'modifiedAt', sortDirection: SortDirection.Descending }
            )
            const existing = (response?.data ?? [])
                .map((module) => module.moduleName)
                .filter((candidateName) => candidateName.startsWith(prefix))
            setExistingContainerNames(Array.from(new Set(existing)))
        } catch (searchError) {
            console.error('Failed to search existing item container modules', searchError)
        }
    }, [templateModuleVersionId])

    const onDownloadSampleCsv = useCallback(() => {
        // Header-only sample CSV: one column per extracted placeholder.
        const csv = `${placeholders.join(',')}\n`
        const element = document.createElement('a')
        element.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
        element.download = `${moduleName ?? 'import'}_sample.csv`
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
        URL.revokeObjectURL(element.href)
    }, [placeholders, moduleName])

    const onPreviewRow = useCallback(
        async (row: Record<string, string>) => {
            if (moduleName === undefined) {
                return
            }
            setErrorMessage(undefined)
            setIsPreviewing(true)
            try {
                // Build the module for this row (same logic as import), then preview that JSON.
                const moduleDtoJson = await ItemPoolService.buildImportedModulePreview(
                    templateModuleVersionId.trim(),
                    row
                )
                const versionId = (JSON.parse(moduleDtoJson) as { version: string }).version
                const previewUrl = await ItemPoolService.previewModuleTemplate(
                    versionId,
                    moduleDtoJson
                )
                window.open(previewUrl, '_blank', 'noopener,noreferrer')
            } catch (e) {
                console.error('Preview failed', e)
                setErrorMessage(e instanceof Error ? e.message : 'Failed to generate the preview.')
            } finally {
                setIsPreviewing(false)
            }
        },
        [moduleName, templateModuleVersionId]
    )

    const onSubmit = useCallback(async () => {
        if (!file || templateModuleVersionId.trim().length === 0) {
            return
        }
        setErrorMessage(undefined)
        setContainers(undefined)
        setIsSubmitting(true)
        try {
            // 1. Upload the CSV to S3; its key is passed to the backend as the spreadsheet path.
            const spreadsheetS3Path = `${S3_IMPORT_PREFIX}/${uuidv4()}-${file.name}`
            await uploadImportSpreadsheetToS3(file, spreadsheetS3Path)

            // 2. Ask the backend to create the IMPORTED item pool.
            const { versionId } = await ItemPoolService.importedItemPool({
                templateModuleVersionId: templateModuleVersionId.trim(),
                spreadsheetS3Path,
            })

            // 3. Show the generated Item Container Modules.
            const generated = await ItemPoolService.getItemPoolItems(versionId)
            setContainers(generated ?? [])
        } catch (e) {
            console.error('Import content failed', e)
            setErrorMessage(
                e instanceof Error ? e.message : 'Failed to import content. Please try again.'
            )
        } finally {
            setIsSubmitting(false)
        }
    }, [file, templateModuleVersionId])

    return (
        <View padding='S400'>
            <H1>Import Content</H1>
            <Spacer height='S200' />
            <Text color='neutral70'>
                Create Item Container Modules from a Template Module and a filled spreadsheet. Enter
                the Template Module version id, upload the CSV, then submit.
            </Text>
            <Spacer height='S300' />
            <Hr size='wide' color='neutral20' />
            <Spacer height='S300' />

            {errorMessage && (
                <>
                    <MessageBanner type={MessageBannerType.Error} dataTestId='import-content-error'>
                        {errorMessage}
                    </MessageBanner>
                    <Spacer height='S300' />
                </>
            )}

            <Col gridGap='S400' width='100%'>
                <Card isElevated width='100%'>
                    <Col gridGap='S200' width='100%'>
                        <Text fontSize='T300' fontWeight='bold'>
                            1. Template module
                        </Text>
                        <Label htmlFor='template-module-version-id'>
                            Template Module version id
                        </Label>
                        <Row gridGap='S200' alignItems='center' width='100%'>
                            <View flex={1}>
                                <Input
                                    id='template-module-version-id'
                                    dataTestId='template-module-version-id'
                                    type='text'
                                    width='100%'
                                    value={templateModuleVersionId}
                                    placeholder='e.g. 123e4567-e89b-12d3-a456-426614174000'
                                    onChange={(e) => {
                                        setTemplateModuleVersionId(e.target.value)
                                        // Editing the id invalidates the previously loaded
                                        // template, so clear its state to force a re-load before
                                        // preview/submit can run (prevents importing against a
                                        // template that was never loaded/validated).
                                        setModuleName(undefined)
                                        setPlaceholders([])
                                        setExistingContainerNames([])
                                        setLoadError(undefined)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            void onLoadTemplate()
                                        }
                                    }}
                                />
                            </View>
                            {isLoadingModule && <Spinner dataTestId='load-template-spinner' />}
                        </Row>
                        {loadError && (
                            <MessageBanner
                                type={MessageBannerType.Error}
                                dataTestId='load-template-error'
                            >
                                {loadError}
                            </MessageBanner>
                        )}
                        {moduleName && (
                            <Col gridGap='S100' dataTestId='template-module-info'>
                                <Text fontSize='T300' fontWeight='bold'>
                                    {moduleName}
                                </Text>
                                {placeholders.length === 0 ? (
                                    <Text fontSize='T100' color='neutral70'>
                                        No placeholders found in this module.
                                    </Text>
                                ) : (
                                    <Col gridGap='S100'>
                                        <Text fontSize='T200' color='neutral70'>
                                            Placeholders ({placeholders.length})
                                        </Text>
                                        <View
                                            dataTestId='template-placeholders'
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                            }}
                                        >
                                            {placeholders.map((name) => (
                                                <View
                                                    key={name}
                                                    backgroundColor='neutral05'
                                                    borderRadius={16}
                                                    padding={{
                                                        top: 'S100',
                                                        bottom: 'S100',
                                                        left: 'S300',
                                                        right: 'S300',
                                                    }}
                                                >
                                                    <Text fontSize='T100' color='neutral90'>
                                                        {name}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                    </Col>
                                )}
                                {placeholders.length > 0 && (
                                    <Button
                                        dataTestId='download-sample-csv'
                                        variant={ButtonVariant.Tertiary}
                                        onClick={onDownloadSampleCsv}
                                    >
                                        Download sample CSV
                                    </Button>
                                )}
                            </Col>
                        )}
                        {existingContainerNames.length > 0 && (
                            <MessageBanner
                                type={MessageBannerType.Warning}
                                dataTestId='existing-containers-warning'
                            >
                                The following Item Container module name(s) already exist and will
                                be overwritten on submit: {existingContainerNames.join(', ')}
                            </MessageBanner>
                        )}
                    </Col>
                </Card>

                <Card isElevated width='100%'>
                    <Col gridGap='S200' width='100%'>
                        <Text fontSize='T300' fontWeight='bold'>
                            2. Spreadsheet
                        </Text>
                        <Label htmlFor='import-content-file-upload'>Spreadsheet (CSV)</Label>
                        <FileUpload
                            id='import-content-file-upload'
                            dragAndDropText='or drag and drop your CSV file here'
                            accept='text/csv'
                            errorText='The upload failed'
                            isMulti={false}
                            onFileAttached={(files) => onFileAttached(files[0])}
                        />
                        <Text fontSize='T100' color='neutral70'>
                            Columns must match the placeholders above. Use “Download sample CSV” for
                            the exact headers.
                        </Text>
                        {csvInfo && (
                            <Col gridGap='S200' dataTestId='csv-validation'>
                                <Text fontSize='T200' color='neutral90'>
                                    {pluralize(csvInfo.rowCount, 'row')} →{' '}
                                    {pluralize(csvInfo.containerCount, 'Item Container module')}
                                </Text>
                                {csvValidation && csvValidation.missing.length > 0 ? (
                                    <MessageBanner
                                        type={MessageBannerType.Warning}
                                        isDismissible={false}
                                        dataTestId='csv-missing-columns'
                                    >
                                        Missing column(s) for placeholder(s):{' '}
                                        {csvValidation.missing.join(', ')}
                                    </MessageBanner>
                                ) : (
                                    <MessageBanner
                                        type={MessageBannerType.Success}
                                        isDismissible={false}
                                        dataTestId='csv-columns-ok'
                                    >
                                        All placeholders have a matching column.
                                    </MessageBanner>
                                )}
                                {csvValidation && csvValidation.extra.length > 0 && (
                                    <MessageBanner
                                        type={MessageBannerType.Informational}
                                        isDismissible={false}
                                        dataTestId='csv-extra-columns'
                                    >
                                        Extra column(s) not used by the template:{' '}
                                        {csvValidation.extra.join(', ')}
                                    </MessageBanner>
                                )}
                            </Col>
                        )}
                    </Col>
                </Card>

                {csvInfo && csvInfo.rows.length > 0 && (
                    <Card isElevated width='100%'>
                        <Col gridGap='S200' width='100%' dataTestId='csv-rows'>
                            <Text fontSize='T300' fontWeight='bold'>
                                3. Review rows ({csvInfo.rowCount})
                            </Text>
                            <Row
                                gridGap='S300'
                                padding='S200'
                                backgroundColor='neutral05'
                                alignItems='center'
                            >
                                {csvInfo.headers.map((header) => (
                                    <View key={header} flex={1}>
                                        <Text fontSize='T100' fontWeight='bold'>
                                            {header}
                                        </Text>
                                    </View>
                                ))}
                                <View width='96px'>
                                    <Text fontSize='T100' fontWeight='bold'>
                                        Preview
                                    </Text>
                                </View>
                            </Row>
                            {csvInfo.rows
                                .slice(pageStart, pageStart + ROWS_PER_PAGE)
                                .map((row, index) => {
                                    const globalIndex = pageStart + index
                                    return (
                                        <Row
                                            key={globalIndex}
                                            gridGap='S300'
                                            alignItems='center'
                                            padding={{
                                                top: 'S200',
                                                bottom: 'S200',
                                                left: 'S200',
                                                right: 'S200',
                                            }}
                                            style={ROW_BORDER}
                                        >
                                            {csvInfo.headers.map((header) => (
                                                <View key={header} flex={1}>
                                                    <Text fontSize='T100' color='neutral90'>
                                                        {truncateCell(row[header])}
                                                    </Text>
                                                </View>
                                            ))}
                                            <View width='96px'>
                                                <Button
                                                    dataTestId={`preview-row-${globalIndex}`}
                                                    size={ButtonSize.Small}
                                                    variant={ButtonVariant.Secondary}
                                                    disabled={!canPreview}
                                                    onClick={() => onPreviewRow(row)}
                                                >
                                                    Preview
                                                </Button>
                                            </View>
                                        </Row>
                                    )
                                })}
                            {csvInfo.rows.length > ROWS_PER_PAGE && (
                                <Row justifyContent='center' alignItems='center' gridGap='S300'>
                                    <Button
                                        dataTestId='csv-rows-prev'
                                        size={ButtonSize.Small}
                                        variant={ButtonVariant.Tertiary}
                                        disabled={previewPage === 0}
                                        onClick={() => setPreviewPage((page) => page - 1)}
                                    >
                                        Previous
                                    </Button>
                                    <Text fontSize='T100' color='neutral70'>
                                        Page {previewPage + 1} of {pageCount}
                                    </Text>
                                    <Button
                                        dataTestId='csv-rows-next'
                                        size={ButtonSize.Small}
                                        variant={ButtonVariant.Tertiary}
                                        disabled={previewPage >= pageCount - 1}
                                        onClick={() => setPreviewPage((page) => page + 1)}
                                    >
                                        Next
                                    </Button>
                                </Row>
                            )}
                            <Row justifyContent='flex-end' gridGap='S300' alignItems='center'>
                                {(isSubmitting || isPreviewing) && (
                                    <Spinner dataTestId='import-content-spinner' />
                                )}
                                <Button
                                    dataTestId='import-content-submit'
                                    variant={ButtonVariant.Primary}
                                    disabled={!canSubmit}
                                    onClick={onSubmit}
                                >
                                    Submit
                                </Button>
                            </Row>
                        </Col>
                    </Card>
                )}

                {containers && (
                    <Card isElevated width='100%'>
                        <Col gridGap='S200' width='100%'>
                            <Text fontSize='T300' fontWeight='bold'>
                                Generated Item Container Modules
                            </Text>
                            {containers.length === 0 ? (
                                <Text>No Item Container Modules were generated.</Text>
                            ) : (
                                <>
                                    <MessageBanner
                                        type={MessageBannerType.Success}
                                        isDismissible={false}
                                        dataTestId='import-success'
                                    >
                                        {`Created ${pluralize(
                                            containers.length,
                                            'Item Container module'
                                        )}.`}
                                    </MessageBanner>
                                    <Col gridGap='S100' dataTestId='generated-item-containers'>
                                        {containers.map((container) => (
                                            <Row
                                                key={container.versionId}
                                                alignItems='center'
                                                justifyContent='space-between'
                                                gridGap='S200'
                                                padding={{ top: 'S200', bottom: 'S200' }}
                                                style={ROW_BORDER}
                                            >
                                                <Col gridGap='S100'>
                                                    <Link
                                                        href={`/module/viewer/${container.versionId}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                    >
                                                        {container.name}
                                                    </Link>
                                                    <Text fontSize='T100' color='neutral70'>
                                                        {container.versionId}
                                                    </Text>
                                                    {(container.locales?.length ?? 0) > 0 && (
                                                        <Text fontSize='T100' color='neutral70'>
                                                            Locales: {container.locales?.join(', ')}
                                                        </Text>
                                                    )}
                                                </Col>
                                                <Button
                                                    variant={ButtonVariant.Tertiary}
                                                    size={ButtonSize.Small}
                                                    dataTestId={`copy-${container.versionId}`}
                                                    onClick={() =>
                                                        navigator.clipboard?.writeText(
                                                            container.versionId
                                                        )
                                                    }
                                                >
                                                    Copy id
                                                </Button>
                                            </Row>
                                        ))}
                                    </Col>
                                </>
                            )}
                        </Col>
                    </Card>
                )}
            </Col>
        </View>
    )
}
